import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import {
  collection,
  doc,
  DocumentData,
  getDoc,
  getFirestore,
  QueryDocumentSnapshot,
  setDoc,
  SnapshotOptions,
} from 'firebase/firestore';

const firebaseApp = initializeApp({
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
});
export const firestore = getFirestore(firebaseApp);
export const auth = getAuth(firebaseApp);

interface FirestoreUser {
  uid: string;
  user: string;
  email: string;
  partner: string;
}

/**
 * Retrieves a user document from Firebase Firestore.
 *
 * @param user - The ID of the user to retrieve.
 * @returns A promise that resolves to the user data if found, or undefined if an error occurs.
 */
export const getUserFromFirebase = async (user: string) => {
  try {
    const userDoc = await getDoc(doc(firestore, 'sss_users', user).withConverter(firestoreUserConverter));
    return userDoc?.data();
  } catch (e) {
    console.error('Error getting document: ', e);
    return undefined;
  }
};

/**
 * Adds a user to the Firestore database.
 *
 * @param {FirestoreUser} userObj - The user object to be added to the Firestore database.
 * @returns {Promise<void>} A promise that resolves when the user is successfully added.
 * @throws Will throw an error if there is an issue adding the document to Firestore.
 */
export const addUserFromFirebase = async (userObj: FirestoreUser) => {
  try {
    const userDocRef = doc(collection(firestore, 'sss_users'), userObj.user);
    const res = await setDoc(userDocRef, userObj);
    console.log('Document written with ID: ', res);
  } catch (e) {
    console.error('Error adding document: ', e);
  }
};

const firestoreUserConverter = {
  toFirestore: (user: FirestoreUser): DocumentData => {
    return user;
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot, options: SnapshotOptions): FirestoreUser => {
    const data = snapshot.data(options);
    return {
      uid: data.uid,
      user: data.user,
      email: data.email,
      partner: data.partner,
    };
  },
};
